import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ProgressIndicator } from './ProgressIndicator';
interface SlideEvidenceProps {
  onNext: () => void;
  expectationAnswer?: number | null;
}
function AnimatedCounter() {
  const [count, setCount] = useState(15);
  const [started, setStarted] = useState(false);
  useEffect(() => {
    const startTimer = setTimeout(() => setStarted(true), 800);
    return () => clearTimeout(startTimer);
  }, []);
  useEffect(() => {
    if (!started || count <= 0) return;
    const interval = setInterval(() => {
      setCount(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 130);
    return () => clearInterval(interval);
  }, [started, count]);
  return <div className="flex items-center justify-center gap-4 sm:gap-6" data-id="element-19">
      <div className="text-center" data-id="element-20">
        <motion.span className="text-7xl sm:text-8xl md:text-9xl font-black tabular-nums glow-teal-strong" style={{
        color: count > 0 ? '#CBD5E1' : '#00B4D8'
      }} animate={{
        color: count === 0 ? '#00B4D8' : '#CBD5E1',
        scale: count === 0 ? [1, 1.08, 1] : 1
      }} transition={{
        duration: 0.5
      }} data-id="element-21">
          {count}
        </motion.span>
        <p className="text-xs sm:text-sm text-slate-500 mt-2 tracking-wide" data-id="element-22">
          median attacks / 6 mo
        </p>
      </div>

      <motion.div className="flex items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.5,
      duration: 0.5
    }} data-id="element-23">
        <svg width="60" height="24" viewBox="0 0 60 24" className="text-teal-500" data-id="element-24">
          <motion.line x1="0" y1="12" x2="44" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 1,
          delay: 0.6
        }} data-id="element-25" />
          <motion.polygon points="44,6 56,12 44,18" fill="currentColor" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1.2,
          duration: 0.3
        }} data-id="element-26" />
        </svg>
      </motion.div>

      <div className="text-center" data-id="element-27">
        <motion.span className={`text-7xl sm:text-8xl md:text-9xl font-black tabular-nums ${count === 0 ? 'glow-teal-strong text-teal-500' : 'text-slate-300'}`} animate={{
        scale: count === 0 ? [1, 1.12, 1] : 1
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} data-id="element-28">
          0
        </motion.span>
        <p className="text-xs sm:text-sm text-slate-500 mt-2 tracking-wide" data-id="element-29">
          after 6 mo treatment
        </p>
      </div>
    </div>;
}
function ExpectationComparison({
  expectationAnswer
}: {
  expectationAnswer: number | null;
}) {
  const expectationLabels = ['~50%', '~70–80%', '≥90%'];
  const expectationValues = [50, 75, 90];
  const observedValue = 100; // representing >90% / attack-free
  const userExpected = expectationAnswer !== null ? expectationValues[expectationAnswer] : null;
  const showSurprise = expectationAnswer !== null && expectationAnswer < 2;
  return <motion.div className="w-full max-w-md mb-8" initial={{
    opacity: 0,
    y: 15
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.6,
    delay: 2.4
  }} data-id="element-30">
      {showSurprise && <motion.p className="text-sm text-teal-600 font-medium text-center mb-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 2.6,
      duration: 0.6
    }} data-id="element-31">
          Many expected lower reductions
        </motion.p>}

      <div className="bg-slate-50 rounded-xl p-5 border border-slate-200 space-y-3" data-id="element-32">
        <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-3" data-id="element-33">
          Expectation vs. observed
        </p>

        {/* User's expectation bar */}
        {userExpected !== null && <div className="space-y-1" data-id="element-34">
            <div className="flex justify-between text-xs" data-id="element-35">
              <span className="text-slate-500" data-id="element-36">Your expectation</span>
              <span className="text-slate-600 font-medium" data-id="element-37">
                {expectationLabels[expectationAnswer!]}
              </span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden" data-id="element-38">
              <motion.div className="h-full rounded-full bg-slate-400" initial={{
            scaleX: 0
          }} animate={{
            scaleX: 1
          }} transition={{
            duration: 0.8,
            delay: 2.8,
            ease: 'easeOut'
          }} style={{
            width: `${userExpected}%`,
            transformOrigin: 'left'
          }} data-id="element-39" />
            </div>
          </div>}

        {/* Observed bar */}
        <div className="space-y-1" data-id="element-40">
          <div className="flex justify-between text-xs" data-id="element-41">
            <span className="text-teal-600 font-medium" data-id="element-42">
              Observed (real-world)
            </span>
            <span className="text-teal-600 font-semibold" data-id="element-43">&gt;90%</span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden" data-id="element-44">
            <motion.div className="h-full rounded-full bg-teal-500" initial={{
            scaleX: 0
          }} animate={{
            scaleX: 1
          }} transition={{
            duration: 1,
            delay: 3.0,
            ease: 'easeOut'
          }} style={{
            width: '95%',
            transformOrigin: 'left'
          }} data-id="element-45" />
          </div>
        </div>
      </div>
    </motion.div>;
}
export function SlideEvidence({
  onNext,
  expectationAnswer = null
}: SlideEvidenceProps) {
  return <div className="relative w-full h-full flex flex-col items-center justify-center px-6" data-id="element-46">
      {/* Subtle radial glow behind counter */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-teal-500/[0.03] blur-3xl pointer-events-none" data-id="element-47" />

      <div className="relative z-10 max-w-2xl mx-auto w-full flex flex-col items-center" data-id="element-48">
        <motion.h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 text-center leading-snug mb-10" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7
      }} data-id="element-49">
          Real-world data is setting a{' '}
          <span className="text-teal-600" data-id="element-50">new benchmark</span>
        </motion.h2>

        {/* Hero counter */}
        <motion.div className="mb-8" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} data-id="element-51">
          <AnimatedCounter data-id="element-52" />
        </motion.div>

        {/* Expectation vs Observed comparison */}
        {expectationAnswer !== null && <ExpectationComparison expectationAnswer={expectationAnswer} data-id="element-53" />}

        {/* Bullet points */}
        <motion.div className="space-y-3 mb-6 w-full max-w-lg" initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: expectationAnswer !== null ? 3.4 : 2.8
      }} data-id="element-54">
          <div className="flex items-start gap-3" data-id="element-55">
            <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-2.5 flex-shrink-0" data-id="element-56" />
            <p className="text-sm sm:text-base text-slate-600" data-id="element-57">
              Median attacks reduced from 15 per 6 months to{' '}
              <strong className="text-slate-900" data-id="element-58">0 after 6 months</strong>
            </p>
          </div>
          <div className="flex items-start gap-3" data-id="element-59">
            <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-2.5 flex-shrink-0" data-id="element-60" />
            <p className="text-sm sm:text-base text-slate-600" data-id="element-61">
              <strong className="text-slate-900" data-id="element-62">77.8%</strong> achieved &gt;90%
              reduction in attack rate
            </p>
          </div>
          <div className="flex items-start gap-3" data-id="element-63">
            <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-2.5 flex-shrink-0" data-id="element-64" />
            <p className="text-sm sm:text-base text-slate-600" data-id="element-65">
              Nearly <strong className="text-slate-900" data-id="element-66">79%</strong> became
              attack-free
            </p>
          </div>
        </motion.div>

        {/* Secondary reference line */}
        <motion.p className="text-xs text-slate-500 text-center mb-6 italic max-w-md" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: expectationAnswer !== null ? 3.8 : 3.2,
        duration: 0.6
      }} data-id="element-67">
          Observed in a nationwide real-world analysis of lanadelumab in routine
          care
        </motion.p>

        <motion.button onClick={onNext} className="px-8 py-3.5 bg-takeda-red text-white font-semibold rounded-lg text-base
                     hover:bg-takeda-darkred transition-all duration-300
                     shadow-lg shadow-red-500/20 
                     focus:outline-none focus:ring-2 focus:ring-takeda-red focus:ring-offset-2 focus:ring-offset-white" initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: expectationAnswer !== null ? 3.6 : 3.0
      }} whileHover={{
        scale: 1.03
      }} whileTap={{
        scale: 0.98
      }} aria-label="What does this mean for practice — proceed to next slide" data-id="element-68">
          What does this mean for practice?
        </motion.button>

        <motion.div className="mt-6" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: expectationAnswer !== null ? 4.0 : 3.4,
        duration: 0.6
      }} data-id="element-69">
          <ProgressIndicator currentStep={5} totalSteps={7} data-id="element-70" />
        </motion.div>
      </div>

      {/* Compliance footer */}
      <motion.footer className="absolute bottom-4 left-0 right-0 text-center px-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: expectationAnswer !== null ? 4.2 : 3.5,
      duration: 0.6
    }} data-id="element-71">
        <p className="text-[10px] text-slate-400 max-w-lg mx-auto" data-id="element-72">
          [Source: Polish nationwide real-world study, Journal of Clinical
          Medicine, 2025]
        </p>
      </motion.footer>
    </div>;
}